import React, {Component} from 'react';
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import Popover from "@mui/material/Popover";
import moment from "moment";
import {HoursMinutesTimeConverter} from "../../../core/prettifier/HoursMinutesTimeConverter";
import {ExerciseType} from "../../../core/models/ExerciseType";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import StarIcon from "@mui/icons-material/Star";
import InsightsIcon from "@mui/icons-material/Insights";
import {ExerciseStatsCalculator} from "../../../core/exercises/ExerciseStatsCalculator";

class RoutineExerciseDetailBloopStatsTab extends Component {
	constructor(props) {
		super(props);
		this.state = {
			exercise: this.props.exercise,
			hasEverBeenShown: false,
			popoverAnchor: null,
			popoverText: null,
		};
	}

	componentWillReceiveProps(nextProps, nextContext) {
		this.setState({
			exercise: nextProps.exercise,
			hasEverBeenShown: !this.state.hasEverBeenShown || nextProps.exercise.routineExerciseId !== this.state.exercise.routineExerciseId ? nextProps.isShowing : true,
		});
	}

	shouldComponentUpdate(nextProps) {
		return nextProps.isShowing || nextProps.exercise.routineExerciseId !== this.state.exercise.routineExerciseId;
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		this.props.updateSwipeableViewsHeightFunc();
	}

	openPopover = (event, text) => {
		this.setState({ popoverAnchor: event.currentTarget, popoverText: text });
	}

	closePopover = () => {
		this.setState({ popoverAnchor: null, popoverText: null });
	}

	renderSummaryCard = (value, label) => (
		<Paper variant="outlined" sx={{
			borderRadius: 2, p: 1.5, textAlign: 'center', height: '100%',
			display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
		}}>
			<Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
				{value}
			</Typography>
			<Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
				{label}
			</Typography>
		</Paper>
	)

	renderStatRow = (label, value, detail, popoverText) => (
		<Box
			sx={{
				display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75,
				cursor: popoverText ? 'pointer' : 'default',
			}}
			onClick={popoverText ? (e) => this.openPopover(e, popoverText) : undefined}
		>
			<Box>
				<Typography variant="body2" sx={{ fontWeight: 500 }}>
					{label}
				</Typography>
				{detail && (
					<Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>
						{detail}
					</Typography>
				)}
			</Box>
			<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
				{value}
			</Typography>
		</Box>
	)

	renderSectionHeader = (icon, label) => (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pt: 1.5, pb: 0.5 }}>
			{icon}
			<Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>
				{label}
			</Typography>
		</Box>
	)

	renderSection = (icon, label, rows) => (
		<Box sx={{
			bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
			borderRadius: 2, px: 2, mb: 1.5,
		}}>
			{this.renderSectionHeader(icon, label)}
			{rows.map((row, idx) => (
				<React.Fragment key={idx}>
					{idx > 0 && <Divider />}
					{this.renderStatRow(row.label, row.value, row.detail, row.popover)}
				</React.Fragment>
			))}
			<Box sx={{ pb: 0.5 }} />
		</Box>
	)

	renderPopover = () => (
		<Popover
			open={!!this.state.popoverAnchor}
			anchorEl={this.state.popoverAnchor}
			onClose={this.closePopover}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			transformOrigin={{ vertical: 'top', horizontal: 'center' }}
		>
			<Typography variant="body2" sx={{ p: 2, maxWidth: 280 }}>
				{this.state.popoverText}
			</Typography>
		</Popover>
	)

	// ── Data calculators (unchanged logic) ──

	getWeightRepsTypeData = (logGroups) => {
		let totalSets = 0, totalWorkouts = 0, totalWeight = 0, totalReps = 0, totalVolume = 0;
		let maxWeightLog = null, maxRepsLog = null, maxVolumeLog = null;
		let averageMaxWeight = 0, averageMaxReps = 0, averageMaxVolume = 0;

		for (const logGroup of logGroups) {
			for (const log of logGroup.logs) {
				totalSets++;
				totalWeight += log.values[0];
				totalReps += log.values[1];
			}
			totalWorkouts++;
			totalVolume += logGroup.totalVolume;

			let topWeightLog = logGroup.logs[logGroup.topWeightLogIdx];
			if (maxWeightLog === null || topWeightLog.values[0] > maxWeightLog.values[0]) maxWeightLog = topWeightLog;
			averageMaxWeight += topWeightLog.values[0];

			let topRepsLog = logGroup.logs[logGroup.topRepsLogIdx];
			if (maxRepsLog === null || topRepsLog.values[1] > maxRepsLog.values[1]) maxRepsLog = topRepsLog;
			averageMaxReps += topRepsLog.values[1];

			let topVolumeLog = logGroup.logs[logGroup.topVolumeLogIdx];
			if (maxVolumeLog === null || topVolumeLog.multiplied > maxVolumeLog.multiplied) maxVolumeLog = topVolumeLog;
			averageMaxVolume += topVolumeLog.multiplied;
		}

		const avgWeight = totalSets > 0 ? totalWeight / totalSets : 0;
		const avgReps = totalSets > 0 ? totalReps / totalSets : 0;
		const avgVolume = totalSets > 0 ? totalVolume / totalSets : 0;
		averageMaxWeight = totalWorkouts > 0 ? averageMaxWeight / totalWorkouts : 0;
		averageMaxReps = totalWorkouts > 0 ? averageMaxReps / totalWorkouts : 0;
		averageMaxVolume = totalWorkouts > 0 ? averageMaxVolume / totalWorkouts : 0;

		return {
			totalSets, totalWorkouts,
			totalWeight: totalWeight.toFixed(1), totalReps: totalReps.toFixed(0), totalVolume: totalVolume.toFixed(1),
			avgWeight: avgWeight.toFixed(1), avgReps: avgReps.toFixed(1), avgVolume: avgVolume.toFixed(1),
			maxWeightLog, maxRepsLog, maxVolumeLog,
			averageMaxWeight: averageMaxWeight.toFixed(1), averageMaxReps: averageMaxReps.toFixed(1), averageMaxVolume: averageMaxVolume.toFixed(1),
		};
	}

	renderWeightRepsType = () => {
		const d = this.getWeightRepsTypeData(this.state.exercise.logGroups);

		return <Box>
			<Grid container spacing={1} sx={{ mb: 1.5 }}>
				<Grid item xs={6}>{this.renderSummaryCard(d.totalSets, 'Total Sets')}</Grid>
				<Grid item xs={6}>{this.renderSummaryCard(d.totalWorkouts, 'Workouts')}</Grid>
			</Grid>

			{this.renderSection(<FitnessCenterIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Totals', [
				{ label: 'Weight', value: d.totalWeight, popover: 'The total amount of weight you have lifted over the filtered time period.' },
				{ label: 'Reps', value: d.totalReps, popover: 'The total amount of reps you have done over the filtered time period.' },
				{ label: 'Volume', value: d.totalVolume, popover: 'The total volume (weight × reps) over the filtered time period.' },
			])}

			{this.renderSection(<TrendingUpIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Averages', [
				{ label: 'Weight', value: d.avgWeight, popover: 'The average weight per set over the filtered time period.' },
				{ label: 'Reps', value: d.avgReps, popover: 'The average reps per set over the filtered time period.' },
				{ label: 'Volume', value: d.avgVolume, popover: 'The average volume per set over the filtered time period.' },
			])}

			{this.renderSection(<EmojiEventsIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Maximums', [
				{ label: 'Weight', value: d.maxWeightLog ? d.maxWeightLog.values[0].toFixed(1) : '0', detail: d.maxWeightLog ? moment(d.maxWeightLog.dateCreated).format('DD/MM/YYYY') : '-', popover: 'The highest weight lifted in any single set over the filtered time period.' },
				{ label: 'Reps', value: d.maxRepsLog ? d.maxRepsLog.values[1].toFixed(0) : '0', detail: d.maxRepsLog ? moment(d.maxRepsLog.dateCreated).format('DD/MM/YYYY') : '-', popover: 'The highest reps in any single set over the filtered time period.' },
				{ label: 'Volume', value: d.maxVolumeLog ? d.maxVolumeLog.multiplied.toFixed(1) : '0', detail: d.maxVolumeLog ? moment(d.maxVolumeLog.dateCreated).format('DD/MM/YYYY') : '-', popover: 'The highest volume (weight × reps) in any single set over the filtered time period.' },
			])}

			{this.renderSection(<StarIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Avg. Best Per Workout', [
				{ label: 'Weight', value: d.averageMaxWeight, popover: 'The average of your best weight per workout over the filtered time period.' },
				{ label: 'Reps', value: d.averageMaxReps, popover: 'The average of your best reps per workout over the filtered time period.' },
				{ label: 'Volume', value: d.averageMaxVolume, popover: 'The average of your best volume per workout over the filtered time period.' },
			])}

			{this.renderEstimated1RM(this.state.exercise.logGroups)}
			{this.renderTrainingPatterns(this.state.exercise.logGroups)}

			{this.renderPopover()}
		</Box>;
	}

	getDistanceTimeTypeData = (logGroups) => {
		let totalSets = 0, totalWorkouts = 0, totalDistance = 0, totalTime = 0;
		let maxDistanceLog = null, maxTimeLog = null;
		let averageMaxDistance = 0, averageMaxTime = 0;

		for (const logGroup of logGroups) {
			for (const log of logGroup.logs) {
				totalSets++;
				totalDistance += log.values[0];
				totalTime += log.values[1];
			}
			totalWorkouts++;

			let topDistanceLog = logGroup.logs[logGroup.topDistanceLogIdx];
			if (maxDistanceLog === null || topDistanceLog.values[0] > maxDistanceLog.values[0]) maxDistanceLog = topDistanceLog;
			averageMaxDistance += topDistanceLog.values[0];

			let topTimeLog = logGroup.logs[logGroup.topTimeLogIdx];
			if (maxTimeLog === null || topTimeLog.values[1] > maxTimeLog.values[1]) maxTimeLog = topTimeLog;
			averageMaxTime += topTimeLog.values[1];
		}

		const avgDistance = totalSets > 0 ? totalDistance / totalSets : 0;
		const avgTime = totalSets > 0 ? totalTime / totalSets : 0;
		averageMaxDistance = totalWorkouts > 0 ? averageMaxDistance / totalWorkouts : 0;
		averageMaxTime = totalWorkouts > 0 ? averageMaxTime / totalWorkouts : 0;

		return {
			totalSets, totalWorkouts,
			totalDistance: totalDistance.toFixed(1), totalTime: HoursMinutesTimeConverter.convertFloatToString(totalTime),
			avgDistance: avgDistance.toFixed(1), avgTime: HoursMinutesTimeConverter.convertFloatToString(avgTime),
			maxDistanceLog, maxTimeLog,
			averageMaxDistance: averageMaxDistance.toFixed(1), averageMaxTime: HoursMinutesTimeConverter.convertFloatToString(averageMaxTime),
		};
	}

	renderDistanceTimeType = () => {
		const d = this.getDistanceTimeTypeData(this.state.exercise.logGroups);

		return <Box>
			<Grid container spacing={1} sx={{ mb: 1.5 }}>
				<Grid item xs={6}>{this.renderSummaryCard(d.totalSets, 'Total Sets')}</Grid>
				<Grid item xs={6}>{this.renderSummaryCard(d.totalWorkouts, 'Workouts')}</Grid>
			</Grid>

			{this.renderSection(<FitnessCenterIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Totals', [
				{ label: 'Distance', value: d.totalDistance, popover: 'The total distance covered over the filtered time period.' },
				{ label: 'Time', value: d.totalTime, popover: 'The total time spent on this exercise over the filtered time period.' },
			])}

			{this.renderSection(<TrendingUpIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Averages', [
				{ label: 'Distance', value: d.avgDistance, popover: 'The average distance per set over the filtered time period.' },
				{ label: 'Time', value: d.avgTime, popover: 'The average time per set over the filtered time period.' },
			])}

			{this.renderSection(<EmojiEventsIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Maximums', [
				{ label: 'Distance', value: d.maxDistanceLog ? d.maxDistanceLog.values[0].toFixed(1) : '0', detail: d.maxDistanceLog ? moment(d.maxDistanceLog.dateCreated).format('DD/MM/YYYY') : '-', popover: 'The maximum distance in any single set.' },
				{ label: 'Time', value: d.maxTimeLog ? HoursMinutesTimeConverter.convertFloatToString(d.maxTimeLog.values[1]) : '0', detail: d.maxTimeLog ? moment(d.maxTimeLog.dateCreated).format('DD/MM/YYYY') : '-', popover: 'The maximum time in any single set.' },
			])}

			{this.renderSection(<StarIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Avg. Best Per Workout', [
				{ label: 'Distance', value: d.averageMaxDistance, popover: 'The average of your best distance per workout.' },
				{ label: 'Time', value: d.averageMaxTime, popover: 'The average of your best time per workout.' },
			])}

			{this.renderTrainingPatterns(this.state.exercise.logGroups)}

			{this.renderPopover()}
		</Box>;
	}

	getTimeTypeData = (logGroups) => {
		let totalSets = 0, totalWorkouts = 0, totalTime = 0;
		let maxTimeLog = null, averageMaxTime = 0;

		for (const logGroup of logGroups) {
			for (const log of logGroup.logs) {
				totalSets++;
				totalTime += log.values[0];
			}
			totalWorkouts++;

			let topTimeLog = logGroup.logs[logGroup.topTimeLogIdx];
			if (maxTimeLog === null || topTimeLog.values[0] > maxTimeLog.values[0]) maxTimeLog = topTimeLog;
			averageMaxTime += topTimeLog.values[0];
		}

		const avgTime = totalSets > 0 ? totalTime / totalSets : 0;
		averageMaxTime = totalWorkouts > 0 ? averageMaxTime / totalWorkouts : 0;

		return {
			totalSets, totalWorkouts,
			totalTime: HoursMinutesTimeConverter.convertFloatToString(totalTime),
			avgTime: HoursMinutesTimeConverter.convertFloatToString(avgTime),
			maxTimeLog,
			averageMaxTime: HoursMinutesTimeConverter.convertFloatToString(averageMaxTime),
		};
	}

	renderTimeType = () => {
		const d = this.getTimeTypeData(this.state.exercise.logGroups);

		return <Box>
			<Grid container spacing={1} sx={{ mb: 1.5 }}>
				<Grid item xs={6}>{this.renderSummaryCard(d.totalSets, 'Total Sets')}</Grid>
				<Grid item xs={6}>{this.renderSummaryCard(d.totalWorkouts, 'Workouts')}</Grid>
			</Grid>

			{this.renderSection(<FitnessCenterIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Totals', [
				{ label: 'Time', value: d.totalTime, popover: 'The total time spent on this exercise over the filtered time period.' },
			])}

			{this.renderSection(<TrendingUpIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Averages', [
				{ label: 'Time', value: d.avgTime, popover: 'The average time per set over the filtered time period.' },
			])}

			{this.renderSection(<EmojiEventsIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Maximums', [
				{ label: 'Time', value: d.maxTimeLog ? HoursMinutesTimeConverter.convertFloatToString(d.maxTimeLog.values[0]) : '0', detail: d.maxTimeLog ? moment(d.maxTimeLog.dateCreated).format('DD/MM/YYYY') : '-', popover: 'The maximum time in any single set.' },
			])}

			{this.renderSection(<StarIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Avg. Best Per Workout', [
				{ label: 'Time', value: d.averageMaxTime, popover: 'The average of your best time per workout.' },
			])}

			{this.renderTrainingPatterns(this.state.exercise.logGroups)}

			{this.renderPopover()}
		</Box>;
	}

	getRepsTypeData = (logGroups) => {
		let totalSets = 0, totalWorkouts = 0, totalReps = 0;
		let maxRepsLog = null, averageMaxReps = 0;

		for (const logGroup of logGroups) {
			for (const log of logGroup.logs) {
				totalSets++;
				totalReps += log.values[0];
			}
			totalWorkouts++;

			let topRepsLog = logGroup.logs[logGroup.topRepsLogIdx];
			if (maxRepsLog === null || topRepsLog.values[0] > maxRepsLog.values[0]) maxRepsLog = topRepsLog;
			averageMaxReps += topRepsLog.values[0];
		}

		const avgReps = totalSets > 0 ? totalReps / totalSets : 0;
		averageMaxReps = totalWorkouts > 0 ? averageMaxReps / totalWorkouts : 0;

		return {
			totalSets, totalWorkouts,
			totalReps: totalReps.toFixed(0),
			avgReps: avgReps.toFixed(1),
			maxRepsLog,
			averageMaxReps: averageMaxReps.toFixed(1),
		};
	}

	renderRepsType = () => {
		const d = this.getRepsTypeData(this.state.exercise.logGroups);

		return <Box>
			<Grid container spacing={1} sx={{ mb: 1.5 }}>
				<Grid item xs={6}>{this.renderSummaryCard(d.totalSets, 'Total Sets')}</Grid>
				<Grid item xs={6}>{this.renderSummaryCard(d.totalWorkouts, 'Workouts')}</Grid>
			</Grid>

			{this.renderSection(<FitnessCenterIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Totals', [
				{ label: 'Reps', value: d.totalReps, popover: 'The total reps over the filtered time period.' },
			])}

			{this.renderSection(<TrendingUpIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Averages', [
				{ label: 'Reps', value: d.avgReps, popover: 'The average reps per set over the filtered time period.' },
			])}

			{this.renderSection(<EmojiEventsIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Maximums', [
				{ label: 'Reps', value: d.maxRepsLog ? d.maxRepsLog.values[0].toFixed(0) : '0', detail: d.maxRepsLog ? moment(d.maxRepsLog.dateCreated).format('DD/MM/YYYY') : '-', popover: 'The highest reps in any single set.' },
			])}

			{this.renderSection(<StarIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Avg. Best Per Workout', [
				{ label: 'Reps', value: d.averageMaxReps, popover: 'The average of your best reps per workout.' },
			])}

			{this.renderTrainingPatterns(this.state.exercise.logGroups)}

			{this.renderPopover()}
		</Box>;
	}

	getWeightTimeTypeData = (logGroups) => {
		let totalSets = 0, totalWorkouts = 0, totalWeight = 0, totalTime = 0;
		let maxWeightLog = null, maxTimeLog = null;
		let averageMaxWeight = 0, averageMaxTime = 0;

		for (const logGroup of logGroups) {
			for (const log of logGroup.logs) {
				totalSets++;
				totalWeight += log.values[0];
				totalTime += log.values[1];
			}
			totalWorkouts++;

			let topWeightLog = logGroup.logs[logGroup.topWeightLogIdx];
			if (maxWeightLog === null || topWeightLog.values[0] > maxWeightLog.values[0]) maxWeightLog = topWeightLog;
			averageMaxWeight += topWeightLog.values[0];

			let topTimeLog = logGroup.logs[logGroup.topTimeLogIdx];
			if (maxTimeLog === null || topTimeLog.values[1] > maxTimeLog.values[1]) maxTimeLog = topTimeLog;
			averageMaxTime += topTimeLog.values[1];
		}

		const avgWeight = totalSets > 0 ? totalWeight / totalSets : 0;
		const avgTime = totalSets > 0 ? totalTime / totalSets : 0;
		averageMaxWeight = totalWorkouts > 0 ? averageMaxWeight / totalWorkouts : 0;
		averageMaxTime = totalWorkouts > 0 ? averageMaxTime / totalWorkouts : 0;

		return {
			totalSets, totalWorkouts,
			totalWeight: totalWeight.toFixed(1), totalTime: HoursMinutesTimeConverter.convertFloatToString(totalTime),
			avgWeight: avgWeight.toFixed(1), avgTime: HoursMinutesTimeConverter.convertFloatToString(avgTime),
			maxWeightLog, maxTimeLog,
			averageMaxWeight: averageMaxWeight.toFixed(1), averageMaxTime: HoursMinutesTimeConverter.convertFloatToString(averageMaxTime),
		};
	}

	renderWeightTimeType = () => {
		const d = this.getWeightTimeTypeData(this.state.exercise.logGroups);

		return <Box>
			<Grid container spacing={1} sx={{ mb: 1.5 }}>
				<Grid item xs={6}>{this.renderSummaryCard(d.totalSets, 'Total Sets')}</Grid>
				<Grid item xs={6}>{this.renderSummaryCard(d.totalWorkouts, 'Workouts')}</Grid>
			</Grid>

			{this.renderSection(<FitnessCenterIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Totals', [
				{ label: 'Weight', value: d.totalWeight, popover: 'The total weight lifted over the filtered time period.' },
				{ label: 'Time', value: d.totalTime, popover: 'The total time spent on this exercise over the filtered time period.' },
			])}

			{this.renderSection(<TrendingUpIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Averages', [
				{ label: 'Weight', value: d.avgWeight, popover: 'The average weight per set over the filtered time period.' },
				{ label: 'Time', value: d.avgTime, popover: 'The average time per set over the filtered time period.' },
			])}

			{this.renderSection(<EmojiEventsIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Maximums', [
				{ label: 'Weight', value: d.maxWeightLog ? d.maxWeightLog.values[0].toFixed(1) : '0', detail: d.maxWeightLog ? moment(d.maxWeightLog.dateCreated).format('DD/MM/YYYY') : '-', popover: 'The highest weight in any single set.' },
				{ label: 'Time', value: d.maxTimeLog ? HoursMinutesTimeConverter.convertFloatToString(d.maxTimeLog.values[1]) : '0', detail: d.maxTimeLog ? moment(d.maxTimeLog.dateCreated).format('DD/MM/YYYY') : '-', popover: 'The maximum time in any single set.' },
			])}

			{this.renderSection(<StarIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Avg. Best Per Workout', [
				{ label: 'Weight', value: d.averageMaxWeight, popover: 'The average of your best weight per workout.' },
				{ label: 'Time', value: d.averageMaxTime, popover: 'The average of your best time per workout.' },
			])}

			{this.renderTrainingPatterns(this.state.exercise.logGroups)}

			{this.renderPopover()}
		</Box>;
	}

	renderTrainingPatterns = (logGroups) => {
		const patterns = ExerciseStatsCalculator.getTrainingPatterns(logGroups);
		if (!patterns) return null;

		const rows = [];
		if (patterns.avgSetsPerSession > 0) rows.push({ label: 'Avg Sets / Session', value: patterns.avgSetsPerSession, popover: 'The average number of sets you perform per workout for this exercise.' });
		if (patterns.sessionsPerMonth) rows.push({ label: 'Sessions / Month', value: patterns.sessionsPerMonth, popover: 'How often you train this exercise per month on average.' });
		if (patterns.repRange && patterns.repRange.low !== patterns.repRange.high) rows.push({ label: 'Typical Rep Range', value: `${patterns.repRange.low}–${patterns.repRange.high}`, popover: 'The rep range you use most often (25th–75th percentile).' });
		if (patterns.mostUsedWeight != null) rows.push({ label: 'Most Used Weight', value: patterns.mostUsedWeight, popover: 'The weight you use most frequently for this exercise.' });
		if (patterns.avgRpe != null) rows.push({ label: 'Average RPE', value: patterns.avgRpe, popover: 'Your average Rate of Perceived Exertion across all logged sets.' });
		if (patterns.avgRir != null) rows.push({ label: 'Average RIR', value: patterns.avgRir, popover: 'Your average Reps in Reserve across all logged sets.' });
		if (rows.length === 0) return null;

		return this.renderSection(<InsightsIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Training Patterns', rows);
	}

	renderEstimated1RM = (logGroups) => {
		const e1rmSessions = ExerciseStatsCalculator.getEstimated1RMPerSession(logGroups);
		if (e1rmSessions.length === 0) return null;

		const current = e1rmSessions[0].value;
		const best = Math.max(...e1rmSessions.map(s => s.value));
		const bestSession = e1rmSessions.find(s => s.value === best);

		const rows = [
			{ label: 'Current', value: current.toFixed(1), detail: moment(e1rmSessions[0].date).format('DD/MM/YYYY'), popover: 'Estimated one-rep max from your most recent session, calculated using the Brzycki formula.' },
			{ label: 'All-Time Best', value: best.toFixed(1), detail: bestSession ? moment(bestSession.date).format('DD/MM/YYYY') : '-', popover: 'Your highest estimated 1RM ever recorded.' },
		];

		return this.renderSection(<EmojiEventsIcon sx={{ fontSize: 16, color: 'text.disabled' }} />, 'Estimated 1RM', rows);
	}

	render() {
		if (!this.state.hasEverBeenShown) {
			return <div />;
		}

		if (this.state.exercise.type.id === ExerciseType.WeightAndReps) return this.renderWeightRepsType();
		if (this.state.exercise.type.id === ExerciseType.WeightAndTime) return this.renderWeightTimeType();
		if (this.state.exercise.type.id === ExerciseType.Reps) return this.renderRepsType();
		if (this.state.exercise.type.id === ExerciseType.DistanceAndTime) return this.renderDistanceTimeType();
		if (this.state.exercise.type.id === ExerciseType.Time) return this.renderTimeType();

		return <React.Fragment />;
	}
}

export default RoutineExerciseDetailBloopStatsTab;
